import _jsx from "@babel/runtime/helpers/jsx";
import PropTypes from 'prop-types';
import React, { memo } from 'react';
import clsx from 'clsx';
import ColoredBackground from '../ColoredBackground/index.js';
import Section from '../Section/index.js';
import Row from '../Row/index.js';
import Col from '../Col/index.js';

/**
 *  This component will serve as a wrapper for the various sections in the applications.
 *  The component handles the presence of the colored background, and all spacing and typography
 *  through the various props. The component was created to reduce the need to add styling
 *  markup in the pages/containers in the applications.
 *
 */

const buildSection = _ref => {
  let {
    initialHeight = 0,
    containerClassname,
    children,
    offset = 0,
    sectionContainerClassNames = 'padding-vertical-5',
    theme,
    bottomStyle,
    className,
    xs,
    sm,
    md,
    lg,
    xsOffset,
    smOffset,
    mdOffset,
    lgOffset,
    ...rest
  } = _ref;
  const containerClasses = clsx('container', containerClassname);
  const containerStyle = initialHeight ? {
    minHeight: `${initialHeight}px`
  } : {};
  const hasStyledContainer = theme || bottomStyle;
  const sectionClassName = clsx(className, {
    'padding-top-6': hasStyledContainer,
    'padding-bottom-6': theme && !bottomStyle
  });
  const section = /*#__PURE__*/React.createElement(Col, Object.assign({
    xs: xs,
    sm: sm,
    md: md ?? offset ? 12 - offset * 2 : null,
    lg: lg,
    xsOffset: xsOffset,
    smOffset: smOffset,
    mdOffset: mdOffset ?? offset !== 0 ? offset : null,
    lgOffset: lgOffset
  }, rest, {
    className: sectionClassName,
    componentClass: Section
  }), children);
  return hasStyledContainer ? /*#__PURE__*/_jsx("div", {
    className: clsx('margin-horizontal-0 margin-bottom-5 padding-top-5', {
      [`${bottomStyle}-container`]: bottomStyle
    })
  }, void 0, /*#__PURE__*/_jsx("div", {
    className: clsx({
      [`bg-${theme}`]: theme,
      inverted: theme?.includes('inverse'),
      [`${bottomStyle}-bottom`]: bottomStyle
    })
  }, void 0, /*#__PURE__*/_jsx("div", {
    className: "container"
  }, void 0, /*#__PURE__*/_jsx(Row, {}, void 0, section)))) : /*#__PURE__*/_jsx("div", {
    className: containerClasses,
    style: containerStyle
  }, void 0, /*#__PURE__*/_jsx(Row, {
    className: sectionContainerClassNames
  }, void 0, section));
};
function PageSection(_ref2) {
  let {
    coloredBackgroundContext,
    initialHeight = 0,
    containerClassname,
    sectionContainerClassNames = 'padding-vertical-5',
    theme,
    bottomStyle,
    className,
    xs,
    sm,
    md,
    lg,
    xsOffset,
    smOffset,
    mdOffset,
    lgOffset,
    ...rest
  } = _ref2;
  const sectionProps = {
    initialHeight,
    containerClassname,
    sectionContainerClassNames,
    className,
    xs,
    sm,
    md,
    lg,
    xsOffset,
    smOffset,
    mdOffset,
    lgOffset,
    ...rest
  };
  return coloredBackgroundContext ? /*#__PURE__*/_jsx(ColoredBackground, {
    className: "container-fluid",
    uiContext: coloredBackgroundContext
  }, void 0, buildSection(sectionProps)) : buildSection({
    ...sectionProps,
    theme,
    bottomStyle
  });
}
export default /*#__PURE__*/memo(PageSection);